import { FILE_META } from "@/components/ui/marks";
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import { toastWithUndo } from "@/components/ui/ToastSurface";
import type { SetAsideFigureResponse } from "@patent-studio/api-types";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { DisclosureHeader, useDisclosure } from "@/components/ui/Disclosure";
import { Panel } from "@/components/ui/Panel";
import { useDismissed } from "@/components/ui/dismiss";
import { FigureDrawing } from "./FigureDrawing";
import { DeletedNavRow } from "./FigureNavigator";

/**
 * Deleted Figures: a figure the attorney deleted is set aside, not erased, and waits here for
 * Restore or Permanently Delete. Delete asks first in a popover; the toast after it offers Undo.
 */

/** How a figure is named on its way out and back: its label, or its file when it had none. */
export function figureName(label: string | null | undefined, filename: string) {
  return label === null || label === undefined ? filename : `FIG. ${label}`;
}

/**
 * The toast after a figure leaves the set, whose Undo restores it and says so once it is back.
 *
 * The words are the site's: Define says the figure was deleted, the organiser where it went.
 */
export function announceDeleted(
  name: string,
  restore: () => Promise<boolean>,
  said = `${name} deleted.`,
): void {
  toastWithUndo(said, () => {
    void restore().then((back) => {
      if (back) toast.success(`${name} restored.`);
    });
  });
}

/** The question's two answers: the way out, which takes the keyboard first, and the delete. */
function QuestionActs({
  cancelRef,
  onCancel,
  onConfirm,
}: {
  cancelRef: React.RefObject<HTMLButtonElement | null>;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="mt-3 flex justify-end gap-2">
      <Button
        ref={cancelRef}
        variant="tertiary"
        size="foot"
        type="button"
        onClick={onCancel}
      >
        Cancel
      </Button>
      <Button variant="danger" size="foot" type="button" onClick={onConfirm}>
        Delete Figure
      </Button>
    </div>
  );
}

/** Delete Figure, and the question it asks under itself before it does anything. */
export function DeleteFigurePopover({
  name,
  onConfirm,
}: {
  /** "FIG. 2", or the file's name for a figure the layout does not place. */
  name: string;
  onConfirm: () => void;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const panelId = useId();
  const headingId = useId();
  const close = useCallback(() => {
    setOpen(false);
  }, []);
  useDismissed(open, panelRef, triggerRef, close);
  // The keyboard lands on the way out, so a question about a deletion is never answered by
  // the same Enter that asked it.
  useEffect(() => {
    if (open) cancelRef.current?.focus();
  }, [open]);

  return (
    <div className="relative ml-auto">
      <Button
        ref={triggerRef}
        variant="danger-outline"
        size="dense"
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={() => {
          setOpen(!open);
        }}
      >
        <Trash2 aria-hidden />
        Delete Figure
      </Button>
      {open && (
        <Panel
          ref={panelRef}
          id={panelId}
          role="dialog"
          aria-labelledby={headingId}
          lift="form"
          pad="snug"
          className="absolute top-full right-0 z-50 mt-1.5 w-menu max-w-[calc(100vw-32px)]"
        >
          <p id={headingId} className="text-[13px] font-bold">
            Are you sure?
          </p>
          <p className="mt-1 text-[12px] text-muted-foreground">
            {`${name} and its numbered components will be removed from the set.`}
          </p>
          <QuestionActs
            cancelRef={cancelRef}
            onCancel={() => {
              close();
              triggerRef.current?.focus();
            }}
            onConfirm={() => {
              close();
              onConfirm();
            }}
          />
        </Panel>
      )}
    </div>
  );
}

/** What each deleted figure offers: Restore, and Permanently Delete once the attorney confirms. */
export interface SetAsideActsProps {
  onRestore: (figure: SetAsideFigureResponse) => void;
  onPurge: (figure: SetAsideFigureResponse) => void;
}

export function SetAsideActs({
  figure,
  onRestore,
  onPurge,
}: SetAsideActsProps & { figure: SetAsideFigureResponse }) {
  const [asking, setAsking] = useState(false);
  const name = figureName(figure.label, figure.original_filename);
  return (
    <div className="flex flex-wrap gap-2">
      {/* Named for the figure, so a reader moving through a list of them can tell the acts apart. */}
      <Button
        variant="secondary"
        size="dense"
        type="button"
        aria-label={`Restore ${name}`}
        onClick={() => {
          onRestore(figure);
        }}
      >
        Restore
      </Button>
      <Button
        variant="danger-outline"
        size="dense"
        type="button"
        aria-label={`Permanently Delete ${name}`}
        onClick={() => {
          setAsking(true);
        }}
      >
        Permanently Delete
      </Button>
      <ConfirmDialog
        open={asking}
        destructive
        title={`Permanently delete ${name}?`}
        description="The figure and its numbered components are removed for good. This cannot be undone."
        actionLabel="Permanently delete"
        onCancel={() => {
          setAsking(false);
        }}
        onConfirm={() => {
          setAsking(false);
          onPurge(figure);
        }}
      />
    </div>
  );
}

/** The fold both lists sit in: closed by default, drawn only while something is deleted. */
export function DeletedFold({
  count,
  children,
}: {
  count: number;
  children: ReactNode;
}) {
  const fold = useDisclosure();
  if (count === 0) return null;
  return (
    <section className="mt-3" data-testid="deleted-figures">
      <DisclosureHeader
        trigger={fold.trigger}
        open={fold.open}
        title={<span className="text-detail font-bold">Deleted Figures</span>}
        meta={
          <span className="text-[11.5px] text-muted-foreground">{`(${String(count)})`}</span>
        }
      />
      <div {...fold.panel} className="mt-2">
        {children}
      </div>
    </section>
  );
}

/** The word a deleted figure wears in place of its label. */
export function DeletedMark() {
  return <span className={FILE_META}>Deleted</span>;
}

/** Define's navigator list: one row per deleted figure, which opens it on the page. */
export function DeletedNavList({
  deleted,
  selectedId,
  onSelect,
  ...acts
}: SetAsideActsProps & {
  deleted: readonly SetAsideFigureResponse[];
  selectedId: string | undefined;
  onSelect: (fileId: string) => void;
}) {
  return (
    <DeletedFold count={deleted.length}>
      <ul className="flex flex-col gap-1">
        {deleted.map((figure) => (
          <li key={figure.file_id} className="flex flex-col gap-1">
            <DeletedNavRow
              figure={figure}
              selected={figure.file_id === selectedId}
              onSelect={onSelect}
            />
            <SetAsideActs figure={figure} {...acts} />
          </li>
        ))}
      </ul>
    </DeletedFold>
  );
}

/** A deleted figure opened on the Define page: where it is, what it was, and the drawing, read-only. */
export function SetAsideFigureView({
  figure,
  ...acts
}: SetAsideActsProps & { figure: SetAsideFigureResponse }) {
  return (
    <section data-testid="define-set-aside" data-file-id={figure.file_id}>
      <Panel
        tone="neutral"
        pad="snug"
        data-testid="set-aside-banner"
        className="mb-3 flex flex-col gap-2"
      >
        <p className="text-detail">
          <strong>This figure is in Deleted Figures.</strong>{" "}
          {`It was ${figureName(figure.label, figure.original_filename)} before deletion. It is not part of the set and will not appear in the draft until you restore it.`}
        </p>
        <SetAsideActs figure={figure} {...acts} />
      </Panel>
      <Panel className="max-w-[420px] shadow-sm">
        <DeletedMark />
        <div data-testid="set-aside-drawing" className="mt-1 opacity-50">
          <FigureDrawing
            thumbnailUrl={figure.thumbnail_url}
            filename={figure.original_filename}
            testId="define-drawing-deleted"
          />
        </div>
      </Panel>
    </section>
  );
}
